import { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/utils";

import {
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "../ui/sidebar";

export type SidebarItemProps = {
  title: string;
  url?: string;
  icon: LucideIcon | React.ComponentType<{ className: string }>;
  onClick?: () => void;
  requiresConfirmation?: boolean;
};

export function SidebarItem({
  title,
  url,
  icon,
  onClick,
  requiresConfirmation,
}: SidebarItemProps) {
  const { pathname } = useLocation();
  const { setOpenMobile } = useSidebar();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isActive = url === pathname;
  const Component = url ? Link : "div";

  function handleClick() {
    if (requiresConfirmation) {
      setIsDialogOpen(true);
      return;
    }
    onClick?.();
  }

  return (
    <>
      <SidebarMenuSubItem key={title} className="bg-transparent">
        <SidebarMenuSubButton
          asChild
          isActive={isActive}
          className={cn(
            isActive &&
              "group border-primary border bg-linear-to-br from-background to-primary/30 from-50% hover:bg-sidebar",
          )}
          onClick={() => {
            setOpenMobile(false);
          }}
        >
          <Component
            to={url || ""}
            onClick={handleClick}
            className={cn(isActive && "!text-primary", "cursor-pointer")}
          >
            <SidebarIcon title={title} icon={icon} />
            <span>{title}</span>
          </Component>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>

      {requiresConfirmation && (
        <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Sair da conta</AlertDialogTitle>

              <AlertDialogDescription>
                Tem certeza que deseja sair? Você precisará fazer login
                novamente para acessar o sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={onClick}>Sair</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

function SidebarIcon({
  title,
  icon: Icon,
}: {
  title: string;
  icon: SidebarItemProps["icon"];
}) {
  /**
   * Special case for the "Entregadores" item, which uses a different icon
   * style.
   */
  if (title === "Entregadores") {
    return (
      <Icon
        className={cn(
          "fill-sidebar-foreground group-data-[active=true]:fill-primary",
        )}
      />
    );
  }

  return <Icon className={cn("group-data-[active=true]:stroke-primary")} />;
}
